import React from 'react';
import { AlertCircleIcon, CheckCircle2Icon, InfoIcon, Loader2Icon } from 'lucide-react';
import { MCP_SUCCESS_MESSAGE, MCP_WORKFLOW_OPTIONS } from '../../data/mcp';
import { BOOK_DEMO_URL } from '../../data/dgpt';
import { GOAL_MAX, useMcpWaitlist, waitlistFieldId, type WaitlistPreviewState } from '../../hooks/useMcpWaitlist';
import { cn } from '../../src/lib/utils';
const inputClass = 'mt-1.5 block w-full rounded-lg border bg-panel px-3.5 py-3 text-[15px] text-text-primary placeholder:text-text-tertiary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lime';
function FieldError({
  id,
  message
}: {
  id: string;
  message?: string;
}) {
  if (!message) return null;
  return <p id={id} className="mt-1.5 flex items-center gap-1.5 text-[13px] text-[#B42318]" data-id="element-401" data-mp="30i9get93ju1x">
      <AlertCircleIcon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" data-id="element-402" data-mp="1u1flzvjm3xyf" />
      {message}
    </p>;
}
function Outcome({
  tone,
  title,
  body,
  onReset,
  isPreview
}: {
  tone: 'success' | 'info';
  title: string;
  body?: string;
  onReset: () => void;
  isPreview: boolean;
}) {
  const Icon = tone === 'success' ? CheckCircle2Icon : InfoIcon;
  return <div role="status" className={cn('rounded-2xl border p-6 sm:p-8', tone === 'success' ? 'border-[#B7E4C7] bg-[#F1FAF4]' : 'border-lime/25 bg-violet-soft')} data-id="element-403" data-mp="344fw7wofp8cw">
      <Icon className={cn('h-7 w-7', tone === 'success' ? 'text-[#1E7A46]' : 'text-lime')} aria-hidden="true" data-id="element-404" data-mp="2u68tahqzcqtw" />
      <p className="mt-4 text-lg font-semibold text-text-primary" data-id="element-405" data-mp="2gf3nsz0xl4bn">{title}</p>
      {body ? <p className="mt-1.5 text-sm leading-relaxed text-text-secondary" data-id="element-406" data-mp="2l6ls9x25xn1d">{body}</p> : null}
      {isPreview ? <p className="mt-3 text-xs font-semibold uppercase tracking-[0.08em] text-[#9A5B13]" data-id="element-407" data-mp="26hpjtyw72808">Design preview · nothing was saved</p> : null}
      <button type="button" onClick={onReset} className="mt-5 inline-flex min-h-[44px] items-center text-sm font-semibold text-lime hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lime" data-id="element-408" data-mp="2bgseqqigmxuh">
        Submit another email
      </button>
    </div>;
}
export function McpWaitlistForm({
  preview = 'live'
}: {
  preview?: WaitlistPreviewState;
}) {
  const {
    values,
    errors,
    status,
    errorKind,
    setField,
    submit,
    reset,
    isPreview
  } = useMcpWaitlist(preview);
  if (status === 'success') {
    return <Outcome tone="success" title={MCP_SUCCESS_MESSAGE} onReset={reset} isPreview={isPreview} data-id="element-409" data-mp="3pg5ev8gfq1bb" />;
  }
  if (status === 'already_registered') {
    return <Outcome tone="info" title="You’re already on the MCP access waitlist." body="No need to sign up again. We’ll email you when access is available." onReset={reset} isPreview={isPreview} data-id="element-410" data-mp="qxglowroqrcn" />;
  }
  const submitting = status === 'submitting';
  return <form noValidate onSubmit={submit} aria-busy={submitting} className="rounded-2xl border border-hairline bg-panel p-6 shadow-sm sm:p-8" data-id="element-411" data-mp="cy72do86nh4g">
      <div data-id="element-412" data-mp="2xo6bi10yrw8n">
        <label htmlFor={waitlistFieldId('email')} className="text-sm font-medium text-text-primary" data-id="element-413" data-mp="2xog0pme6pzda">
          Work email <span className="text-[#B42318]" aria-hidden="true" data-id="element-414" data-mp="1d7osd4vkts5z">*</span>
        </label>
        <input id={waitlistFieldId('email')} type="email" autoComplete="email" required value={values.email} onChange={event => setField('email', event.target.value)} placeholder="you@company.com" aria-invalid={Boolean(errors.email)} aria-describedby={errors.email ? `${waitlistFieldId('email')}-error` : undefined} className={cn(inputClass, errors.email ? 'border-[#D92D20]' : 'border-hairline')} data-id="element-415" data-mp="1h7h2vn4j68dz" />
        <FieldError id={`${waitlistFieldId('email')}-error`} message={errors.email} data-id="element-416" data-mp="24yw8borlt5tg" />
      </div>

      <div className="mt-5" data-id="element-417" data-mp="29vv1a3zj76q2">
        <label htmlFor={waitlistFieldId('workflow')} className="text-sm font-medium text-text-primary" data-id="element-418" data-mp="29w4pbm10n3t4">
          Intended workflow <span className="text-[#B42318]" aria-hidden="true" data-id="element-419" data-mp="fiws5npkig4b">*</span>
        </label>
        <select id={waitlistFieldId('workflow')} required value={values.workflow} onChange={event => setField('workflow', event.target.value)} aria-invalid={Boolean(errors.workflow)} aria-describedby={errors.workflow ? `${waitlistFieldId('workflow')}-error` : undefined} className={cn(inputClass, 'appearance-auto', errors.workflow ? 'border-[#D92D20]' : 'border-hairline', !values.workflow && 'text-text-tertiary')} data-id="element-420" data-mp="fkzi67qq0991">
          <option value="" disabled data-id="element-421" data-mp="2xliiqaia9sei">
            Choose a workflow
          </option>
          {MCP_WORKFLOW_OPTIONS.map(option => <option key={option.value} value={option.value} className="text-text-primary" data-id="element-422" data-mp="37dy138xhsizx">
              {option.label}
            </option>)}
        </select>
        <FieldError id={`${waitlistFieldId('workflow')}-error`} message={errors.workflow} data-id="element-423" data-mp="32kv1ok61afbt" />
      </div>

      <div className="mt-5" data-id="element-424" data-mp="29y1ylv5693yn">
        <div className="flex items-baseline justify-between gap-2" data-id="element-425" data-mp="29ytvraef39r1">
          <label htmlFor={waitlistFieldId('goal')} className="text-sm font-medium text-text-primary" data-id="element-426" data-mp="pfzspvjpol8o">
            What would you like an agent to help you do? <span className="font-normal text-text-tertiary" data-id="element-427" data-mp="u7hv8o1dutvr">(optional)</span>
          </label>
          <span className={cn('shrink-0 text-xs tabular-nums', values.goal.length > GOAL_MAX ? 'text-[#B42318]' : 'text-text-tertiary')} data-id="element-428" data-mp="fiv47p2e8dop">
            {values.goal.length}/{GOAL_MAX}
          </span>
        </div>
        <textarea id={waitlistFieldId('goal')} rows={4} value={values.goal} onChange={event => setField('goal', event.target.value)} aria-invalid={Boolean(errors.goal)} aria-describedby={errors.goal ? `${waitlistFieldId('goal')}-error` : undefined} className={cn(inputClass, 'resize-y', errors.goal ? 'border-[#D92D20]' : 'border-hairline')} data-id="element-429" data-mp="1870q4f56o8q4" />
        <FieldError id={`${waitlistFieldId('goal')}-error`} message={errors.goal} data-id="element-430" data-mp="3jy4r3abd13od" />
      </div>

      {status === 'error' ? <div role="alert" className="mt-5 flex gap-2.5 rounded-xl border border-[#F5C2C0] bg-[#FFF6F5] px-4 py-3 text-sm text-text-primary" data-id="element-431" data-mp="1uemx19sfdybs">
          <AlertCircleIcon className="mt-0.5 h-4 w-4 shrink-0 text-[#B42318]" aria-hidden="true" data-id="element-432" data-mp="1ph4lrcqxr70m" />
          <p data-id="element-433" data-mp="1kns9m23g0sor">
            {errorKind === 'unavailable' ? 'Waitlist signups aren’t available online yet. ' : 'We couldn’t save your signup. Please try again. '}
            <a href={BOOK_DEMO_URL} target="_blank" rel="noopener noreferrer" className="font-semibold underline underline-offset-2" data-id="element-434" data-mp="1bpg998crygj4">
              Talk to our team instead
            </a>
            .
          </p>
        </div> : null}

      <button type="submit" disabled={submitting} className="mt-6 inline-flex min-h-[48px] w-full items-center justify-center gap-2 rounded-lg bg-lime px-6 text-[15px] font-semibold text-white transition-colors hover:bg-[#2B1E7A] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lime focus-visible:ring-offset-2 disabled:cursor-wait disabled:opacity-80" data-id="element-435" data-mp="11s278drlluac">
        {submitting ? <>
            <Loader2Icon className="h-4 w-4 animate-spin motion-reduce:animate-none" aria-hidden="true" data-id="element-436" data-mp="33mzr9wzaz9dz" />
            Joining…
          </> : 'Join the waitlist'}
      </button>
      <p className="mt-3 text-xs text-text-tertiary" data-id="element-437" data-mp="3dcj86x3ggt67">We’ll only use your email to contact you about MCP access.</p>
    </form>;
}